import { useState, useRef, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Vault, UploadCloud, FileText, Download, LogOut, Check, ArrowRight } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const FLOW = ["Under Review", "Assessment In Progress", "Offer Ready", "Funded"];

const goldBtn =
  "sheen-btn inline-flex items-center gap-3 text-[#4D4232] text-[11px] font-semibold font-mono-accent uppercase tracking-[0.25em] px-8 py-4 shadow-[0_10px_25px_rgba(201,162,39,0.4)] hover:scale-[1.02] transition-transform disabled:opacity-40 disabled:cursor-not-allowed";
const goldBg = { background: "linear-gradient(135deg, #a87f24 0%, #e5c158 50%, #d4af37 100%)" };
const strip = { background: "linear-gradient(90deg, #8c6d3f 0%, #e5c158 50%, #8c6d3f 100%)" };

const Dashboard = () => {
  const [email, setEmail] = useState(() => sessionStorage.getItem("em_dash_email") || "");
  const [reference, setReference] = useState(() => sessionStorage.getItem("em_dash_ref") || "");
  const [app, setApp] = useState(null);
  const [statuses, setStatuses] = useState(FLOW);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [uploading, setUploading] = useState(0);
  const fileRef = useRef(null);

  useEffect(() => {
    const em = sessionStorage.getItem("em_dash_email");
    const rf = sessionStorage.getItem("em_dash_ref");
    if (em && rf) load(em, rf);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const load = async (em, ref) => {
    setLoading(true);
    setError("");
    try {
      const res = await axios.post(`${API}/applications/lookup`, { email: em, reference: ref });
      setApp(res.data.application);
      setStatuses(res.data.statuses || FLOW);
      sessionStorage.setItem("em_dash_email", em);
      sessionStorage.setItem("em_dash_ref", ref);
    } catch (e) {
      sessionStorage.removeItem("em_dash_email");
      sessionStorage.removeItem("em_dash_ref");
      setApp(null);
      setError(
        e.response?.status === 404
          ? "No application found for that email and seat number."
          : "Could not load your application. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    sessionStorage.removeItem("em_dash_email");
    sessionStorage.removeItem("em_dash_ref");
    setApp(null);
    setReference("");
  };

  const upload = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("email", email);
    fd.append("file", file);
    setUploading(1);
    try {
      await axios.post(`${API}/applications/${app.reference}/documents`, fd, {
        onUploadProgress: (e) => setUploading(Math.round((e.loaded / (e.total || 1)) * 100)),
      });
      toast.success("Document uploaded to your vault file.");
      await load(email, app.reference);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed. Please try again.");
    } finally {
      setUploading(0);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const download = async (doc) => {
    try {
      const res = await axios.get(
        `${API}/documents/${doc.id}/download?email=${encodeURIComponent(email)}&reference=${encodeURIComponent(app.reference)}`,
        { responseType: "blob" }
      );
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = doc.original_filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Download failed. Please try again.");
    }
  };

  if (!app) {
    return (
      <main data-testid="dashboard-login" className="pt-20 min-h-screen flex items-center justify-center px-6">
        <div className="relative bg-[#fffdf9] border border-[#e3d3ac] p-10 lg:p-14 max-w-md w-full shadow-[0_30px_80px_rgba(45,26,11,0.22)]">
          <span className="absolute bottom-0 left-0 right-0 h-[3px]" style={strip} />
          <span
            className="mx-auto w-11 h-11 rotate-45 flex items-center justify-center mb-8 shadow-[0_8px_20px_rgba(201,162,39,0.4)]"
            style={goldBg}
          >
            <Vault className="-rotate-45 w-5 h-5 text-white" />
          </span>
          <h1 className="font-serif font-semibold text-3xl text-cream text-center">
            Your <span className="italic gold-text">vault file.</span>
          </h1>
          <p className="mt-4 text-sm text-sand text-center leading-relaxed">
            Enter the email you applied with and your seat number (e.g. EM-XXXXXXXX)
            from your confirmation to view your status and upload documents.
          </p>
          <form
            className="mt-8 space-y-5"
            onSubmit={(e) => {
              e.preventDefault();
              load(email, reference);
            }}
          >
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Email address"
              data-testid="dashboard-email-input"
              className="w-full bg-[#faf7ef] border border-[#c9a227]/30 px-5 py-4 text-sm text-cream placeholder:text-stone/60 focus:outline-none focus:border-[#c9a227] transition-colors"
            />
            <input
              type="text"
              required
              value={reference}
              onChange={(e) => setReference(e.target.value)}
              placeholder="Seat number (EM-XXXXXXXX)"
              data-testid="dashboard-reference-input"
              className="w-full bg-[#faf7ef] border border-[#c9a227]/30 px-5 py-4 text-sm text-cream placeholder:text-stone/60 focus:outline-none focus:border-[#c9a227] transition-colors"
            />
            {error && (
              <p data-testid="dashboard-login-error" className="text-xs text-red-700">{error}</p>
            )}
            <button type="submit" disabled={loading} data-testid="dashboard-login-button" className={`${goldBtn} w-full justify-center`} style={goldBg}>
              {loading ? "Opening…" : "View My Application"} <ArrowRight className="w-4 h-4" />
            </button>
          </form>
        </div>
      </main>
    );
  }

  const currentStep = Math.max(0, FLOW.indexOf(app.status));
  const declined = app.status === "Declined";

  return (
    <main data-testid="client-dashboard" className="pt-20 min-h-screen px-6 lg:px-12 pb-24">
      <div className="max-w-4xl mx-auto pt-14">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-10">
          <div>
            <p className="flex items-center gap-3 text-[10px] font-mono-accent uppercase tracking-[0.4em] text-gold mb-3">
              <span className="w-1.5 h-1.5 rotate-45 bg-[#c9a227]" />
              Your Vault File
            </p>
            <h1 className="font-serif font-semibold text-3xl sm:text-4xl text-[#f3e5c8]">
              Welcome back, <span className="italic gold-text">{app.artist_label_name}.</span>
            </h1>
          </div>
          <button
            onClick={logout}
            data-testid="dashboard-logout-button"
            className="btn-outline-lux inline-flex items-center gap-2 text-champagne text-[11px] font-mono-accent uppercase tracking-[0.2em] px-5 py-3 hover:text-gold"
          >
            <LogOut className="w-3.5 h-3.5" /> Log Out
          </button>
        </div>

        {/* STATUS */}
        <div className="relative bg-[#fffdf9] border border-[#e3d3ac] p-8 lg:p-12 shadow-[0_20px_60px_rgba(45,26,11,0.12)] mb-8">
          <span className="absolute bottom-0 left-0 right-0 h-[3px]" style={strip} />
          <div className="flex flex-wrap items-baseline justify-between gap-3 mb-8">
            <span className="font-mono-accent text-sm tracking-[0.15em] text-gold">{app.reference}</span>
            <span
              data-testid="dashboard-status-badge"
              className={`text-[10px] font-mono-accent uppercase tracking-[0.25em] px-4 py-2 ${
                declined ? "bg-red-900/10 text-red-800 border border-red-800/30" : "text-[#4D4232]"
              }`}
              style={declined ? {} : goldBg}
            >
              {app.status}
            </span>
          </div>
          {!declined && (
            <div className="flex items-center">
              {FLOW.map((s, i) => (
                <div key={s} className="flex-1 flex items-center last:flex-none">
                  <div className="flex flex-col items-center text-center">
                    <span
                      data-testid={`status-step-${i}`}
                      className={`w-9 h-9 rotate-45 flex items-center justify-center border transition-all duration-500 ${
                        i < currentStep
                          ? "border-[#c9a227] bg-[#c9a227]"
                          : i === currentStep
                          ? "border-[#c9a227] shadow-[0_0_18px_rgba(201,162,39,0.5)]"
                          : "border-[#e3d3ac]"
                      }`}
                      style={i === currentStep ? goldBg : {}}
                    >
                      <span className="-rotate-45">
                        {i < currentStep ? (
                          <Check className="w-4 h-4 text-white" />
                        ) : (
                          <span className={`w-1.5 h-1.5 rotate-45 ${i === currentStep ? "bg-white" : "bg-[#e3d3ac]"}`} />
                        )}
                      </span>
                    </span>
                    <span className={`mt-3 text-[9px] font-mono-accent uppercase tracking-[0.15em] ${i <= currentStep ? "text-champagne" : "text-stone/60"}`}>
                      {s}
                    </span>
                  </div>
                  {i < FLOW.length - 1 && (
                    <span className={`flex-1 h-px mx-2 mb-6 ${i < currentStep ? "bg-[#c9a227]" : "bg-[#e3d3ac]"}`} />
                  )}
                </div>
              ))}
            </div>
          )}
          <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-x-8 gap-y-3 text-sm">
            <div><span className="block text-[9px] font-mono-accent uppercase tracking-[0.25em] text-stone mb-1">Funding Type</span><span className="text-sand">{app.funding_type}</span></div>
            <div><span className="block text-[9px] font-mono-accent uppercase tracking-[0.25em] text-stone mb-1">Amount</span><span className="text-sand">{app.amount}</span></div>
            <div><span className="block text-[9px] font-mono-accent uppercase tracking-[0.25em] text-stone mb-1">Entity</span><span className="text-sand">{app.entity_type}</span></div>
            <div><span className="block text-[9px] font-mono-accent uppercase tracking-[0.25em] text-stone mb-1">Submitted</span><span className="text-sand">{app.timestamp ? new Date(app.timestamp).toLocaleDateString() : "—"}</span></div>
          </div>
        </div>

        {/* DOCUMENTS */}
        <div className="relative bg-[#fffdf9] border border-[#e3d3ac] p-8 lg:p-12 shadow-[0_20px_60px_rgba(45,26,11,0.12)]">
          <span className="absolute bottom-0 left-0 right-0 h-[3px]" style={strip} />
          <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
            <h2 className="font-serif text-2xl text-cream">Vault <span className="italic gold-text">documents</span></h2>
            <span className="text-[10px] font-mono-accent uppercase tracking-[0.25em] text-stone">
              PDF · PNG · JPG · TXT · CSV — 15MB max
            </span>
          </div>
          <p className="text-sm text-sand leading-relaxed mb-8">
            Royalty statements, bank statements, and ID help us move faster. Upload
            them here and they attach directly to your application.
          </p>

          <input
            ref={fileRef}
            type="file"
            accept=".pdf,.png,.jpg,.jpeg,.webp,.txt,.csv"
            className="hidden"
            data-testid="dashboard-file-input"
            onChange={(e) => upload(e.target.files?.[0])}
          />
          <button
            onClick={() => fileRef.current?.click()}
            disabled={uploading > 0}
            data-testid="dashboard-upload-button"
            className="w-full border-2 border-dashed border-[#c9a227]/50 hover:border-[#c9a227] transition-colors py-10 flex flex-col items-center gap-3 text-champagne"
          >
            <UploadCloud className="w-7 h-7" />
            <span className="text-[11px] font-mono-accent uppercase tracking-[0.25em]">
              {uploading > 0 ? `Uploading… ${uploading}%` : "Choose a document to upload"}
            </span>
          </button>
          {uploading > 0 && (
            <div className="mt-4 h-1 bg-[#e3d3ac]/50">
              <div className="h-full transition-all duration-300" style={{ ...goldBg, width: `${uploading}%` }} />
            </div>
          )}

          <div className="mt-8 space-y-3" data-testid="dashboard-documents-list">
            {app.documents.length === 0 ? (
              <p className="text-sm text-stone">No documents uploaded yet.</p>
            ) : (
              app.documents.map((d) => (
                <div
                  key={d.id}
                  data-testid={`dashboard-document-${d.id}`}
                  className="flex items-center justify-between gap-4 border border-[#e3d3ac] bg-white px-5 py-4"
                >
                  <div className="flex items-center gap-4 min-w-0">
                    <FileText className="w-5 h-5 text-gold shrink-0" />
                    <div className="min-w-0">
                      <p className="text-sm text-cream truncate">{d.original_filename}</p>
                      <p className="text-[10px] font-mono-accent text-stone mt-0.5">
                        {(d.size / 1024).toFixed(1)} KB · {new Date(d.created_at).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => download(d)}
                    data-testid={`dashboard-download-${d.id}`}
                    className="inline-flex items-center gap-2 text-[10px] font-mono-accent uppercase tracking-[0.2em] text-champagne hover:text-gold transition-colors shrink-0"
                  >
                    <Download className="w-4 h-4" /> Download
                  </button>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </main>
  );
};

export default Dashboard;
